/**
 * GPU-accelerated SDF volume calculation.
 *
 * Mirrors the CPU implementation in {@link SDFVolumeCalculation} but
 * runs the per-voxel work in a fragment shader and reduces the
 * per-cell contributions to a single scalar on the GPU. Only the
 * final result is read back, avoiding the multi-MB readback a
 * hybrid (GPU compute + CPU sum) approach would require.
 *
 * Pipeline:
 *   Pass 1  - per-voxel contribution shader. For each SDF voxel
 *             cell outputs the cell's mm^3 contribution to the
 *             total volume (R32F). All-inside cells emit
 *             voxelVolumeMm3; all-outside emit 0; boundary cells
 *             emit insideCount * subVoxelVolumeMm3 or, when
 *             sub-sampling missed a thin above-threshold region,
 *             the analytical tetrahedral fallback value.
 *   Pass 2  - 2x2x2 reduction shader. Each fragment sums up to 8
 *             source texels. Ping-pongs between two R32F 3D
 *             textures until every axis is 1.
 *   Pass 3  - readback of 1 float. That is the total volume in mm^3.
 *
 * Numerical match with the CPU reference is bounded by FP32
 * precision in the shader: the tree-reduction accumulation, plus
 * FP32-vs-FP64 sub-sample boundary classification flips. The
 * deviation is rasterizer-dependent and is pinned by the parity
 * test in SDFVolumeRenderer.browser.test.ts. The fallback path is
 * kept as a continuous float contribution (not quantised to a
 * sub-sample count) so cells where sub-sampling missed thin
 * features still contribute their analytical estimate exactly.
 */

import { gl } from '../../../Engine';
import { Renderer } from '../../../core/render/Renderer';
import { RenderStorage } from '../../../core/render/RenderStorage';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { VertexArrayObject } from '../../../core/utils/VertexArrayObject';
import { UniformFloat } from '../../../core/utils/uniforms/UniformFloat';
import { UniformInt } from '../../../core/utils/uniforms/UniformInt';
import { Grid } from '../../../data/common/Grid';
import { SignedDistanceField } from '../../../core/models/SignedDistanceField';
import { assertNoPendingGlError } from '../../gl/GLUtilities';
import {
    assertFramebufferComplete,
    createR32FTexture3D,
} from './Texture3DUtils';

/**
 * Number of trilinear sub-samples per axis used at boundary cells.
 * Must match the default used by the CPU implementation
 * ({@link _internalCalculateVolumeMm3}) so the two results agree.
 */
const SUB_SAMPLES = 3;

/**
 * Normalised inside/outside threshold matching the CPU constant of
 * 127.5 against an R8 SDF whose values are [0, 255]: 127.5 / 255.
 */
const NORMALISED_THRESHOLD = 127.5 / 255.0;

/** Bytes per texel of an R32F (single 32-bit float) render target. */
const BYTES_PER_R32F_TEXEL = 4;

/**
 * Default cap on the transient R32F memory the GPU volume path may
 * hold at once. The cell grid is processed in Z-chunks sized to stay
 * under this, so an oversized structure runs in several chunks rather
 * than one large allocation that could fail outright or lose the GL
 * context. Overridable via the constructor for testing.
 */
const GPU_MEMORY_BUDGET_BYTES = 256 * 1024 * 1024;

class VolumeContributionShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
        precision highp float;
        in vec2 position;
        void main() {
            gl_Position = vec4(position, 0.0, 1.0);
        }`;

    /**
     * Fragment shader. One fragment per voxel cell (x, y) at the
     * Z slice given by uCellZ. Outputs the cell's mm^3 contribution
     * directly as a float.
     */
    private static readonly FRAGMENT_CONTENT = `#version 300 es
        precision highp float;
        precision highp sampler3D;

        uniform sampler3D uSdf;
        uniform int uCellZ;
        uniform float uVoxelVolumeMm3;
        uniform float uSubVoxelVolumeMm3;
        uniform float uThreshold;
        uniform int uSubSamples;

        layout(location = 0) out float outVolume;

        // Trilinear interpolation between 8 corner values.
        float trilinear(
            float v000, float v100, float v010, float v110,
            float v001, float v101, float v011, float v111,
            float fx, float fy, float fz
        ) {
            float v00 = mix(v000, v100, fx);
            float v01 = mix(v001, v101, fx);
            float v10 = mix(v010, v110, fx);
            float v11 = mix(v011, v111, fx);
            float v0 = mix(v00, v10, fy);
            float v1 = mix(v01, v11, fy);
            return mix(v0, v1, fz);
        }

        // Analytical tetrahedra fallback. Mirrors
        // computeCornerTetrahedraVolume in SDFVolumeCalculation.ts.
        // Produces a fractional mm^3 contribution that captures
        // thin above-threshold features the sub-sample loop missed.
        float cornerTetrahedraVolume(
            float v000, float v100, float v010, float v110,
            float v001, float v101, float v011, float v111,
            float threshold, float voxelVolume
        ) {
            float minV = min(min(min(v000, v100), min(v010, v110)),
                             min(min(v001, v101), min(v011, v111)));
            if (minV > threshold) {
                return voxelVolume;
            }

            float volume = 0.0;
            float vals[8] = float[8](v000, v100, v010, v110,
                                     v001, v101, v011, v111);
            float adj0[8] = float[8](v100, v000, v000, v100,
                                     v000, v001, v001, v101);
            float adj1[8] = float[8](v010, v110, v110, v010,
                                     v011, v111, v111, v011);
            float adj2[8] = float[8](v001, v101, v011, v111,
                                     v101, v100, v010, v110);

            for (int i = 0; i < 8; i++) {
                float val = vals[i];
                if (val <= threshold) {
                    continue;
                }
                float a0 = adj0[i];
                float a1 = adj1[i];
                float a2 = adj2[i];
                float t0 = (a0 > threshold) ? 1.0 : (val - threshold) / (val - a0);
                float t1 = (a1 > threshold) ? 1.0 : (val - threshold) / (val - a1);
                float t2 = (a2 > threshold) ? 1.0 : (val - threshold) / (val - a2);
                volume += (1.0 / 6.0) * t0 * t1 * t2 * voxelVolume;
            }
            return volume;
        }

        void main() {
            int x = int(gl_FragCoord.x);
            int y = int(gl_FragCoord.y);
            int z = uCellZ;

            // Sample 8 SDF corners for cell (x, y, z) -> (x+1, y+1, z+1).
            // texelFetch on R8 returns normalised [0, 1].
            float v000 = texelFetch(uSdf, ivec3(x,     y,     z),     0).r;
            float v100 = texelFetch(uSdf, ivec3(x + 1, y,     z),     0).r;
            float v010 = texelFetch(uSdf, ivec3(x,     y + 1, z),     0).r;
            float v110 = texelFetch(uSdf, ivec3(x + 1, y + 1, z),     0).r;
            float v001 = texelFetch(uSdf, ivec3(x,     y,     z + 1), 0).r;
            float v101 = texelFetch(uSdf, ivec3(x + 1, y,     z + 1), 0).r;
            float v011 = texelFetch(uSdf, ivec3(x,     y + 1, z + 1), 0).r;
            float v111 = texelFetch(uSdf, ivec3(x + 1, y + 1, z + 1), 0).r;

            float t = uThreshold;
            float maxV = max(max(max(v000, v100), max(v010, v110)),
                             max(max(v001, v101), max(v011, v111)));
            float minV = min(min(min(v000, v100), min(v010, v110)),
                             min(min(v001, v101), min(v011, v111)));

            // All inside -> full voxel volume.
            if (minV > t) {
                outVolume = uVoxelVolumeMm3;
                return;
            }
            // All outside -> zero.
            if (maxV <= t) {
                outVolume = 0.0;
                return;
            }

            // Boundary cell. Sub-sample with trilinear interpolation,
            // counting samples above the threshold.
            int insideCount = 0;
            float invSub = 1.0 / float(uSubSamples);
            for (int sz = 0; sz < uSubSamples; sz++) {
                float fz = (float(sz) + 0.5) * invSub;
                for (int sy = 0; sy < uSubSamples; sy++) {
                    float fy = (float(sy) + 0.5) * invSub;
                    for (int sx = 0; sx < uSubSamples; sx++) {
                        float fx = (float(sx) + 0.5) * invSub;
                        float v = trilinear(
                            v000, v100, v010, v110,
                            v001, v101, v011, v111,
                            fx, fy, fz
                        );
                        if (v > t) {
                            insideCount++;
                        }
                    }
                }
            }

            if (insideCount > 0) {
                outVolume = float(insideCount) * uSubVoxelVolumeMm3;
                return;
            }

            // Sub-sampling missed a thin above-threshold region.
            // Fall back to the corner-tetrahedra analytical estimate,
            // contributed as a continuous mm^3 value.
            outVolume = cornerTetrahedraVolume(
                v000, v100, v010, v110,
                v001, v101, v011, v111,
                t, uVoxelVolumeMm3
            );
        }`;

    public readonly uCellZ: UniformInt;
    public readonly uVoxelVolumeMm3: UniformFloat;
    public readonly uSubVoxelVolumeMm3: UniformFloat;
    public readonly uThreshold: UniformFloat;
    public readonly uSubSamples: UniformInt;
    private readonly uSdfLocation: WebGLUniformLocation | null;

    public constructor() {
        super(
            VolumeContributionShader.VERTEX_CONTENT,
            VolumeContributionShader.FRAGMENT_CONTENT,
            'position'
        );
        this.uCellZ = new UniformInt('uCellZ');
        this.uVoxelVolumeMm3 = new UniformFloat('uVoxelVolumeMm3');
        this.uSubVoxelVolumeMm3 = new UniformFloat('uSubVoxelVolumeMm3');
        this.uThreshold = new UniformFloat('uThreshold');
        this.uSubSamples = new UniformInt('uSubSamples');
        super.storeUniformLocations(
            this.uCellZ,
            this.uVoxelVolumeMm3,
            this.uSubVoxelVolumeMm3,
            this.uThreshold,
            this.uSubSamples
        );
        this.uSdfLocation = gl.getUniformLocation(this.programID, 'uSdf');
    }

    public bindSdfSampler(textureUnit: number): void {
        if (this.uSdfLocation) {
            gl.uniform1i(this.uSdfLocation, textureUnit);
        }
    }
}

class VolumeReductionShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
        precision highp float;
        in vec2 position;
        void main() {
            gl_Position = vec4(position, 0.0, 1.0);
        }`;

    /**
     * 2x2x2 reduction. Each output fragment sums up to 8 source
     * texels with float arithmetic. For odd source dimensions the
     * out-of-range samples are skipped, which is correct because
     * the source texture's valid region was restricted by viewport
     * in the previous pass.
     */
    private static readonly FRAGMENT_CONTENT = `#version 300 es
        precision highp float;
        precision highp sampler3D;

        uniform sampler3D uSrc;
        uniform int uOutZ;
        uniform int uSrcSizeX;
        uniform int uSrcSizeY;
        uniform int uSrcSizeZ;

        layout(location = 0) out float outSum;

        void main() {
            int ox = int(gl_FragCoord.x);
            int oy = int(gl_FragCoord.y);
            int oz = uOutZ;

            float sum = 0.0;
            for (int dz = 0; dz < 2; dz++) {
                int sz = oz * 2 + dz;
                if (sz >= uSrcSizeZ) continue;
                for (int dy = 0; dy < 2; dy++) {
                    int sy = oy * 2 + dy;
                    if (sy >= uSrcSizeY) continue;
                    for (int dx = 0; dx < 2; dx++) {
                        int sx = ox * 2 + dx;
                        if (sx >= uSrcSizeX) continue;
                        sum += texelFetch(uSrc, ivec3(sx, sy, sz), 0).r;
                    }
                }
            }
            outSum = sum;
        }`;

    public readonly uOutZ: UniformInt;
    public readonly uSrcSizeX: UniformInt;
    public readonly uSrcSizeY: UniformInt;
    public readonly uSrcSizeZ: UniformInt;
    private readonly uSrcLocation: WebGLUniformLocation | null;

    public constructor() {
        super(
            VolumeReductionShader.VERTEX_CONTENT,
            VolumeReductionShader.FRAGMENT_CONTENT,
            'position'
        );
        this.uOutZ = new UniformInt('uOutZ');
        this.uSrcSizeX = new UniformInt('uSrcSizeX');
        this.uSrcSizeY = new UniformInt('uSrcSizeY');
        this.uSrcSizeZ = new UniformInt('uSrcSizeZ');
        super.storeUniformLocations(
            this.uOutZ,
            this.uSrcSizeX,
            this.uSrcSizeY,
            this.uSrcSizeZ
        );
        this.uSrcLocation = gl.getUniformLocation(this.programID, 'uSrc');
    }

    public bindSampler(textureUnit: number): void {
        if (this.uSrcLocation) {
            gl.uniform1i(this.uSrcLocation, textureUnit);
        }
    }
}

interface VolumeShaders {
    readonly contribution: VolumeContributionShader;
    readonly reduction: VolumeReductionShader;
}

export class SDFVolumeRenderer implements Renderer {
    /**
     * Null when the GPU path is unavailable: either
     * `EXT_color_buffer_float` is missing (R32F render targets need
     * it) or the shaders would not compile. Callers fall back to CPU.
     */
    private readonly shaders: VolumeShaders | null;

    /**
     * Cap on the transient R32F memory the volume path holds at once;
     * the cell grid is chunked to stay under it. Defaults to
     * GPU_MEMORY_BUDGET_BYTES. Settable (rather than a constructor
     * argument, which RenderStorage's no-arg construction forbids) so
     * tests can force the multi-chunk path on a small grid. Test-only:
     * production callers must leave the default in place.
     */
    public budgetBytes: number = GPU_MEMORY_BUDGET_BYTES;

    /**
     * Construction never throws. RenderStorage caches renderers only
     * after the constructor returns, so a throwing one would never be
     * cached and every volume query would retry the failed shader
     * compile — and would escape the caller's CPU fallback entirely.
     */
    public constructor() {
        this.shaders = SDFVolumeRenderer.createShaders();
    }

    private static createShaders(): VolumeShaders | null {
        if (gl.getExtension('EXT_color_buffer_float') === null) {
            return null;
        }

        let contribution: VolumeContributionShader | null = null;
        try {
            contribution = new VolumeContributionShader();
            return { contribution, reduction: new VolumeReductionShader() };
        } catch (e) {
            // The second constructor can throw after the first
            // succeeded, so release what was already allocated.
            contribution?.delete();
            const msg = e instanceof Error ? e.message : String(e);
            console.warn('GPU volume shaders unavailable, using CPU: ' + msg);
            return null;
        }
    }

    public get supported(): boolean {
        return this.shaders !== null;
    }

    /**
     * Compute structure volume in mm^3 on the GPU.
     *
     * Returns null if the GPU path cannot produce a trustworthy
     * result: missing float-render extension, failed texture or
     * framebuffer allocation, an incomplete render target, or a
     * rejected readback. Null means "fall back to the CPU
     * implementation"; it is never a volume, and `strictNullChecks`
     * obliges callers to narrow before using the value. Returns 0
     * only when the SDF is too small for any voxel cell to exist
     * (dim <= 1 on any axis).
     */
    public calculateVolumeMm3(
        grid: Grid,
        sdf: SignedDistanceField
    ): number | null {
        // Grid permits zero resolutions, the CPU reference rejects
        // them. Stays outside the try below, which would turn this
        // into a silent CPU fallback.
        if (grid.resX <= 0 || grid.resY <= 0 || grid.resZ <= 0) {
            throw new Error('All voxel resolutions must be positive');
        }

        const shaders = this.shaders;
        if (!shaders) {
            return null;
        }
        const dimX = sdf.size[0];
        const dimY = sdf.size[1];
        const dimZ = sdf.size[2];
        if (dimX <= 1 || dimY <= 1 || dimZ <= 1) {
            return 0;
        }

        // Boxes whose corners are voxel centers are the "cells" that
        // contribute volume.
        const cellX = dimX - 1;
        const cellY = dimY - 1;
        const cellZ = dimZ - 1;
        const voxelVolumeMm3 = grid.resX * grid.resY * grid.resZ;
        const subVoxelVolumeMm3 =
            voxelVolumeMm3 / (SUB_SAMPLES * SUB_SAMPLES * SUB_SAMPLES);

        // Size the chunk from the exact texA + texB byte cost. texB
        // (the reduction scratch) is fixed in X/Y and adds ceil(D/2)
        // layers for D layers of texA; bounding ceil(D/2) by (D+1)/2
        // makes this closed form a safe under-estimate that never picks
        // a chunk over budget, and it is exact for odd D. maxLayers < 1
        // means even a single layer does not fit, so fall back to CPU.
        const texBSliceX = Math.max(1, Math.ceil(cellX / 2));
        const texBSliceY = Math.max(1, Math.ceil(cellY / 2));
        const texASliceBytes = cellX * cellY * BYTES_PER_R32F_TEXEL;
        const texBSliceBytes = texBSliceX * texBSliceY * BYTES_PER_R32F_TEXEL;
        const maxLayers = Math.floor(
            (this.budgetBytes - texBSliceBytes / 2) /
                (texASliceBytes + texBSliceBytes / 2)
        );
        if (maxLayers < 1) {
            console.warn(
                'GPU volume: a single-layer chunk exceeds the memory ' +
                    'budget, using CPU'
            );
            return null;
        }
        const chunkDepthZ = Math.min(cellZ, maxLayers);

        // Save the caller's framebuffer binding and viewport so we
        // leave GL state how we found it. The reduction loop shrinks
        // the viewport to ~1x1, and volumeCm3 is a synchronous getter
        // on the shared context, so a following draw would otherwise be
        // clipped to that. Mirrors SdfGenerator.renderSdf.
        const callerFbo = gl.getParameter(
            gl.FRAMEBUFFER_BINDING
        ) as WebGLFramebuffer | null;
        const callerViewport = gl.getParameter(gl.VIEWPORT) as Int32Array;

        const fbo = gl.createFramebuffer();
        if (!fbo) {
            console.error('GPU volume: failed to create framebuffer');
            return null;
        }

        // Process the cell grid in Z-chunks so the transient R32F
        // footprint stays under the budget regardless of structure
        // size. texA holds one chunk (chunkDepthZ layers); texB is its
        // half-size reduction scratch. Both are sized for one chunk and
        // reused across chunks; per-chunk partial sums accumulate on the
        // CPU. A structure that fits in a single chunk reduces exactly
        // as the unchunked path did.
        const texA = createR32FTexture3D(cellX, cellY, chunkDepthZ);
        const texB = createR32FTexture3D(
            texBSliceX,
            texBSliceY,
            Math.max(1, Math.ceil(chunkDepthZ / 2))
        );
        if (!texA || !texB) {
            console.error('GPU volume: failed to create R32F textures');
            gl.deleteFramebuffer(fbo);
            if (texA) {
                gl.deleteTexture(texA);
            }
            if (texB) {
                gl.deleteTexture(texB);
            }
            return null;
        }

        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.disable(gl.BLEND);
        gl.disable(gl.DEPTH_TEST);
        gl.disable(gl.STENCIL_TEST);
        gl.disable(gl.CULL_FACE);

        // Acquired inside the try: first-time VAO/VBO allocation can
        // throw, and that must fall back to CPU rather than escape and
        // leak the framebuffer and textures allocated above.
        let quadVAO: VertexArrayObject | undefined;

        try {
            quadVAO = RenderStorage.getQuadVAO();
            quadVAO.bind(0);
            gl.activeTexture(gl.TEXTURE0);

            let totalMm3 = 0;
            let contributionTargetChecked = false;
            let reductionTargetChecked = false;

            for (
                let chunkStartZ = 0;
                chunkStartZ < cellZ;
                chunkStartZ += chunkDepthZ
            ) {
                const chunkCellsZ = Math.min(chunkDepthZ, cellZ - chunkStartZ);

                // Pass 1: per-cell contributions for this chunk into
                // texA layers 0..chunkCellsZ-1. uCellZ is the absolute
                // cell index so the shader reads the correct SDF corners;
                // the target layer is chunk-local.
                shaders.contribution.start();
                shaders.contribution.bindSdfSampler(0);
                shaders.contribution.uVoxelVolumeMm3.loadFloat(voxelVolumeMm3);
                shaders.contribution.uSubVoxelVolumeMm3.loadFloat(
                    subVoxelVolumeMm3
                );
                shaders.contribution.uThreshold.loadFloat(NORMALISED_THRESHOLD);
                shaders.contribution.uSubSamples.loadInt(SUB_SAMPLES);
                gl.bindTexture(gl.TEXTURE_3D, sdf.texture);
                gl.viewport(0, 0, cellX, cellY);

                for (let k = 0; k < chunkCellsZ; k++) {
                    shaders.contribution.uCellZ.loadInt(chunkStartZ + k);
                    gl.framebufferTextureLayer(
                        gl.FRAMEBUFFER,
                        gl.COLOR_ATTACHMENT0,
                        texA,
                        0,
                        k
                    );
                    if (!contributionTargetChecked) {
                        assertFramebufferComplete(
                            'SDFVolumeRenderer contribution pass target'
                        );
                        contributionTargetChecked = true;
                    }
                    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
                }
                shaders.contribution.stop();

                // Pass 2: reduce (cellX, cellY, chunkCellsZ) down to a
                // single texel, ping-ponging texA <-> texB. Reads only
                // layers < chunkCellsZ, so stale data from a previous,
                // deeper chunk in the reused texture is never summed.
                shaders.reduction.start();
                shaders.reduction.bindSampler(0);

                let srcTex = texA;
                let dstTex = texB;
                let srcX = cellX;
                let srcY = cellY;
                let srcZ = chunkCellsZ;

                while (srcX > 1 || srcY > 1 || srcZ > 1) {
                    const dstX = Math.max(1, Math.ceil(srcX / 2));
                    const dstY = Math.max(1, Math.ceil(srcY / 2));
                    const dstZ = Math.max(1, Math.ceil(srcZ / 2));

                    shaders.reduction.uSrcSizeX.loadInt(srcX);
                    shaders.reduction.uSrcSizeY.loadInt(srcY);
                    shaders.reduction.uSrcSizeZ.loadInt(srcZ);

                    gl.bindTexture(gl.TEXTURE_3D, srcTex);
                    gl.viewport(0, 0, dstX, dstY);

                    for (let z = 0; z < dstZ; z++) {
                        shaders.reduction.uOutZ.loadInt(z);
                        gl.framebufferTextureLayer(
                            gl.FRAMEBUFFER,
                            gl.COLOR_ATTACHMENT0,
                            dstTex,
                            0,
                            z
                        );
                        if (!reductionTargetChecked) {
                            assertFramebufferComplete(
                                'SDFVolumeRenderer reduction pass target'
                            );
                            reductionTargetChecked = true;
                        }
                        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
                    }

                    const tmp = srcTex;
                    srcTex = dstTex;
                    dstTex = tmp;
                    srcX = dstX;
                    srcY = dstY;
                    srcZ = dstZ;
                }
                shaders.reduction.stop();

                // Read this chunk's scalar (at (0,0,0) of srcTex after
                // the loop) and accumulate. Cross-chunk summation is
                // FP64, so the total is at least as accurate as a single
                // FP32 tree over the whole volume.
                gl.framebufferTextureLayer(
                    gl.FRAMEBUFFER,
                    gl.COLOR_ATTACHMENT0,
                    srcTex,
                    0,
                    0
                );
                assertFramebufferComplete('SDFVolumeRenderer readback source');

                // RGBA/FLOAT is the combination guaranteed readable from
                // a float colour buffer; RED/FLOAT is
                // implementation-defined and rejected by some drivers.
                // Only .r carries the sum. Pre-filled with NaN so a
                // readback that writes nothing without raising an error
                // is still detected.
                const pixel = new Float32Array(4).fill(Number.NaN);
                // Fails on an error left by the passes above rather than
                // discarding it: a failed draw leaves partially reduced
                // data that reads back as a finite, plausible, wrong
                // volume.
                assertNoPendingGlError('SDFVolumeRenderer render passes');
                gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.FLOAT, pixel);
                const readbackError = gl.getError();
                if (readbackError !== gl.NO_ERROR) {
                    throw new Error(
                        `SDFVolumeRenderer: volume readback failed ` +
                            `(error 0x${readbackError.toString(16)})`
                    );
                }
                if (!Number.isFinite(pixel[0])) {
                    throw new Error(
                        'SDFVolumeRenderer: volume readback produced no value'
                    );
                }

                totalMm3 += pixel[0];
            }

            return totalMm3;
        } catch (e) {
            // Any GL failure past allocation — incomplete framebuffer,
            // rejected readback, lost context — must surface as null so
            // the caller falls back to the CPU implementation. Returning
            // the zero-initialised readback buffer instead would report
            // 0 cm^3 for a real structure.
            const msg =
                e instanceof Error
                    ? `${e.message}\n${e.stack ?? ''}`
                    : String(e);
            console.warn('GPU volume failed, falling back to CPU: ' + msg);
            return null;
        } finally {
            quadVAO?.unbind(0);
            gl.useProgram(null);
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_3D, null);
            gl.bindFramebuffer(gl.FRAMEBUFFER, callerFbo);
            gl.viewport(
                callerViewport[0],
                callerViewport[1],
                callerViewport[2],
                callerViewport[3]
            );
            gl.deleteFramebuffer(fbo);
            gl.deleteTexture(texA);
            gl.deleteTexture(texB);
        }
    }

    public delete(): void {
        this.shaders?.contribution.delete();
        this.shaders?.reduction.delete();
    }
}
